/**
 * WebUSB-FTDI Manager for PalmSens Device Communication on Android Chrome
 * Handles FTDI device connection using WebUSB API as alternative to Web Serial
 * Based on webusb-ftdi implementations from Shaped Technologies and Leaphy Robotics
 *
 * Note: This is specifically for Android Chrome where Web Serial API may not work properly
 * but WebUSB does work for FTDI devices.
 */

import { getSerialConfig } from 'methodscript-communication';
import { logger } from 'shared-monaco-methodscript';

import { ConnectionState } from './connection-state.js';
import {
    DeviceBusyError,
    DeviceCommunicationError,
    DeviceConnectionError,
    DeviceDisconnectedError,
    DeviceError,
    DeviceNotSupportedError,
    toErrorMessage,
} from './device-errors.js';
import type { UsbFtdiDeviceInfo } from './device-info.js';

const DEVICE_TYPE = 'webusb-ftdi' as const;

// FTDI USB Control Request Constants
const FTDI_SIO_RESET = 0x00;
const FTDI_SIO_MODEM_CTRL = 0x01;
const FTDI_SIO_SET_FLOW_CTRL = 0x02;
const FTDI_SIO_SET_BAUDRATE = 0x03;
const FTDI_SIO_SET_DATA = 0x04;
const FTDI_SIO_SET_LATENCY_TIMER = 0x09;
const FTDI_SIO_SET_BITMODE = 0x0b;

// Reset types
const FTDI_SIO_RESET_SIO = 0x00;

// Bitmode
const FTDI_BITMODE_RESET = 0x00;

// Reference clocks
const BAUDRATE_REF_BASE = 3.0e6;
const BAUDRATE_REF_HIGH = 12.0e6;

export class WebUSBFTDIManager extends EventTarget {
    private device: USBDevice | null;
    private state: ConnectionState;
    private interfaceNumber: number | null;
    private endpointIn: USBEndpoint | null;
    private endpointOut: USBEndpoint | null;
    private keepReading: boolean;
    private isSupported: boolean;

    constructor() {
        super();
        this.device = null;
        this.state = ConnectionState.Disconnected;
        this.interfaceNumber = null;
        this.endpointIn = null;
        this.endpointOut = null;
        this.keepReading = true;

        // Check WebUSB support
        this.isSupported = 'usb' in navigator;
    }

    /**
     * Check if WebUSB is supported in the current browser
     */
    public isWebUSBFTDISupported(): boolean {
        return this.isSupported;
    }

    /**
     * Get current connection state
     */
    public getState(): ConnectionState {
        return this.state;
    }

    /**
     * Get current device info
     */
    public getDeviceInfo(): UsbFtdiDeviceInfo | null {
        if (!this.device) {
            return null;
        }

        return {
            vendorId: this.device.vendorId,
            productId: this.device.productId,
            productName: this.device.productName ?? undefined,
            manufacturerName: this.device.manufacturerName ?? undefined,
            serialNumber: this.device.serialNumber ?? undefined,
        };
    }

    /**
     * Set connection state and dispatch event
     */
    private setState(newState: ConnectionState): void {
        if (this.state === newState) {
            return;
        }

        this.state = newState;
        this.dispatchEvent(new CustomEvent('stateChange', { detail: { state: newState } }));
    }

    /**
     * Detect if device is legacy FTDI (FT232R, FT245R)
     */
    private isLegacyDevice(): boolean {
        if (!this.device) return false;
        return this.device.deviceVersionMajor < 2;
    }

    /**
     * Detect if device is modern FTDI (FT232H, FT2232H, FT4232H)
     */
    private isModernDevice(): boolean {
        if (!this.device) return false;
        return [7, 8, 9].includes(this.device.deviceVersionMajor);
    }

    /**
     * Detect if device has MPSSE capability
     */
    private hasMPSSE(): boolean {
        if (!this.device) return false;
        return [5, 7, 8, 9].includes(this.device.deviceVersionMajor);
    }

    /**
     * Convert baud rate to FTDI divisor values
     * Follows FTDI logic for different device types
     */
    private convertBaudrate(baudRate: number): { value: number; index: number } {
        let refclock, hispeed;

        // Check if baud rate is valid
        if (baudRate < Math.floor((2 * BAUDRATE_REF_BASE) / (2 * 16384 + 1))) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'Baud rate too low');
        }

        if (baudRate > BAUDRATE_REF_BASE) {
            if (!this.isModernDevice() || baudRate > BAUDRATE_REF_HIGH) {
                throw new DeviceConnectionError(DEVICE_TYPE, 'Baud rate too high');
            }
            refclock = BAUDRATE_REF_HIGH;
            hispeed = true;
        } else {
            refclock = BAUDRATE_REF_BASE;
            hispeed = false;
        }

        const amAdjustUp = [0, 0, 0, 1, 0, 3, 2, 1];
        const amAdjustDn = [0, 0, 0, 1, 0, 1, 2, 3];
        const fracCode = [0, 3, 2, 4, 1, 5, 6, 7];

        let divisor = Math.floor((refclock * 8) / baudRate);
        if (this.isLegacyDevice()) {
            divisor -= amAdjustDn[divisor & 7];
        }

        let bestDivisor = 0;
        let bestBaudDiff = 0;

        for (let i = 0; i <= 1; i++) {
            let tryDivisor = divisor + i;

            if (!hispeed) {
                if (tryDivisor <= 8) {
                    tryDivisor = 8;
                } else if (this.isLegacyDevice() && tryDivisor < 12) {
                    tryDivisor = 12;
                } else if (tryDivisor < 16) {
                    tryDivisor = 16;
                } else {
                    if (this.isLegacyDevice()) {
                        tryDivisor += amAdjustUp[tryDivisor & 7];
                        if (tryDivisor > 0x1fff8) {
                            tryDivisor = 0x1fff8;
                        }
                    } else {
                        if (tryDivisor > 0x1ffff) {
                            tryDivisor = 0x1ffff;
                        }
                    }
                }
            }

            const baudEstimate = Math.floor((refclock * 8 + Math.floor(tryDivisor / 2)) / tryDivisor);
            const baudDiff = Math.abs(baudEstimate - baudRate);

            if (i === 0 || baudDiff < bestBaudDiff) {
                bestDivisor = tryDivisor;
                bestBaudDiff = baudDiff;
                if (baudDiff === 0) break;
            }
        }

        let encodedDivisor = (bestDivisor >> 3) | (fracCode[bestDivisor & 7] << 14);

        if (encodedDivisor === 1) {
            encodedDivisor = 0;
        } else if (encodedDivisor === 0x4001) {
            encodedDivisor = 1;
        }

        const value = encodedDivisor & 0xffff;
        let index;

        if (this.hasMPSSE()) {
            index = (encodedDivisor >> 8) & 0xffff;
            index &= 0xff00;
            index |= this.interfaceNumber ?? 0;
        } else {
            index = (encodedDivisor >> 16) & 0xffff;
        }

        if (hispeed) {
            index |= 1 << 9;
        }

        return { value, index };
    }

    /**
     * Request device selection and connect
     */
    public async connect(baudRate?: number): Promise<void> {
        logger.debug('[WebUSB-FTDI] Connect called, current state:', this.state);

        if (!this.isSupported) {
            throw new DeviceNotSupportedError(DEVICE_TYPE, 'WebUSB is not supported in this browser.');
        }

        if (this.state === ConnectionState.Connected) {
            logger.warn('[WebUSB-FTDI] Already connected, disconnecting first...');
            await this.disconnect();
        }

        if (this.state === ConnectionState.Connecting) {
            throw new DeviceBusyError(DEVICE_TYPE);
        }

        this.setState(ConnectionState.Connecting);

        try {
            // Request device from user
            logger.debug('[WebUSB-FTDI] Requesting device selection...');
            this.device = await navigator.usb.requestDevice({
                filters: [
                    // FTDI devices
                    { vendorId: 0x0403 }, // FTDI vendor ID
                ],
            });

            logger.debug('[WebUSB-FTDI] Device selected:', {
                productName: this.device.productName,
                manufacturerName: this.device.manufacturerName,
                serialNumber: this.device.serialNumber,
                vendorId: `0x${this.device.vendorId.toString(16)}`,
                productId: `0x${this.device.productId.toString(16)}`,
                deviceVersion: `${this.device.deviceVersionMajor}.${this.device.deviceVersionMinor}.${this.device.deviceVersionSubminor}`,
            });

            // Auto-detect baud rate based on device name if not specified
            if (baudRate === undefined) {
                const description = this.device.productName ?? '';
                const { baudRate: detectedBaudRate } = getSerialConfig({ description });
                baudRate = detectedBaudRate;
                logger.debug(`[WebUSB-FTDI] Auto-detected baud rate: ${baudRate} for device "${description}"`);
            } else {
                logger.debug(`[WebUSB-FTDI] Using specified baud rate: ${baudRate}`);
            }

            await this.openDevice();
            await this.initFTDI();
            await this.configureFTDI(baudRate);

            this.setState(ConnectionState.Connected);

            // Start reading from device
            logger.debug('[WebUSB-FTDI] Starting read loop...');
            void this.startReading();

            logger.debug('[WebUSB-FTDI] Connection established successfully');
            this.dispatchEvent(new CustomEvent('connected', { detail: this.getDeviceInfo() }));
        } catch (error) {
            logger.error('[WebUSB-FTDI] Connection failed:', error);
            this.setState(ConnectionState.Error);
            await this.cleanup();
            throw new DeviceConnectionError(DEVICE_TYPE, `Failed to connect: ${toErrorMessage(error)}`, error);
        }
    }

    /**
     * Open USB device, select configuration, claim interface, and find bulk endpoints
     */
    private async openDevice(): Promise<void> {
        if (!this.device) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'No device selected');
        }

        logger.debug('[WebUSB-FTDI] Opening device...');
        if (!this.device.opened) {
            await this.device.open();
            logger.debug('[WebUSB-FTDI] Device opened successfully');
        }

        // Select configuration
        if (this.device.configuration === null) {
            logger.debug('[WebUSB-FTDI] Selecting configuration 1...');
            await this.device.selectConfiguration(1);
        }

        // Find the appropriate interface (usually interface 0 for FTDI)
        const { configuration } = this.device;
        if (!configuration || configuration.interfaces.length === 0) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'No interfaces found on device');
        }

        const iface = configuration.interfaces[0];
        this.interfaceNumber = iface.interfaceNumber;

        logger.debug('[WebUSB-FTDI] Claiming interface', this.interfaceNumber);
        await this.device.claimInterface(this.interfaceNumber);

        // Find bulk endpoints
        const { alternate } = iface;
        logger.debug('[WebUSB-FTDI] Finding endpoints...');
        for (const endpoint of alternate.endpoints) {
            if (endpoint.type === 'bulk') {
                if (endpoint.direction === 'in') {
                    this.endpointIn = endpoint;
                    logger.debug('[WebUSB-FTDI] Found IN endpoint:', endpoint.endpointNumber);
                } else if (endpoint.direction === 'out') {
                    this.endpointOut = endpoint;
                    logger.debug('[WebUSB-FTDI] Found OUT endpoint:', endpoint.endpointNumber);
                }
            }
        }

        if (!this.endpointIn || !this.endpointOut) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'Failed to find required bulk endpoints');
        }
    }

    /**
     * Initialize FTDI device by resetting it and setting bitmode
     */
    private async initFTDI(): Promise<void> {
        if (!this.device || this.interfaceNumber === null) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'Device not opened');
        }

        logger.debug('[WebUSB-FTDI] Initializing FTDI device...');

        // Reset device
        logger.debug('[WebUSB-FTDI] Resetting device...');
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_RESET,
                value: FTDI_SIO_RESET_SIO,
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );

        // Set bitmode to reset
        logger.debug('[WebUSB-FTDI] Setting bitmode...');
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_SET_BITMODE,
                value: FTDI_BITMODE_RESET,
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );
    }

    /**
     * Configure FTDI device: baud rate, data format, flow control, latency timer, and modem control
     */
    private async configureFTDI(baudRate: number): Promise<void> {
        if (!this.device || this.interfaceNumber === null) {
            throw new DeviceConnectionError(DEVICE_TYPE, 'Device not initialized');
        }

        // Set baud rate
        logger.debug('[WebUSB-FTDI] Setting baud rate to', baudRate);
        const { value, index } = this.convertBaudrate(baudRate);
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_SET_BAUDRATE,
                value,
                index,
            },
            new Uint8Array([]),
        );

        // Set data format: 8 data bits, no parity, 1 stop bit
        logger.debug('[WebUSB-FTDI] Setting data format (8N1)...');
        const dataBits = 8;
        const parity = 0; // None
        const stopBits = 0; // 1 stop bit
        const dataConfig = (dataBits & 0x0f) | (parity << 8) | (stopBits << 11);

        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_SET_DATA,
                value: dataConfig,
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );

        // Set flow control to none
        logger.debug('[WebUSB-FTDI] Setting flow control...');
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_SET_FLOW_CTRL,
                value: 0x0000, // No flow control
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );

        // Set latency timer (lower = more responsive)
        logger.debug('[WebUSB-FTDI] Setting latency timer...');
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_SET_LATENCY_TIMER,
                value: 1, // 1ms latency
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );

        // Set DTR and RTS
        logger.debug('[WebUSB-FTDI] Setting modem control signals...');
        await this.device.controlTransferOut(
            {
                requestType: 'vendor',
                recipient: 'device',
                request: FTDI_SIO_MODEM_CTRL,
                value: 0x0303, // DTR=1, RTS=1
                index: this.interfaceNumber,
            },
            new Uint8Array([]),
        );
    }

    /**
     * Disconnect from device
     */
    public async disconnect(): Promise<void> {
        if (this.state === ConnectionState.Disconnected) {
            return;
        }

        await this.cleanup();
        this.setState(ConnectionState.Disconnected);
        this.dispatchEvent(new CustomEvent('disconnected'));
    }

    /**
     * Clean up device resources
     */
    private async cleanup(): Promise<void> {
        logger.debug('[WebUSB-FTDI] Cleanup called');

        this.keepReading = false;

        // Release interface
        try {
            if (this.device?.opened && this.interfaceNumber !== null) {
                logger.debug('[WebUSB-FTDI] Releasing interface...');
                await this.device.releaseInterface(this.interfaceNumber);
            }
        } catch (error) {
            logger.warn('[WebUSB-FTDI] Error releasing interface:', error);
        } finally {
            this.interfaceNumber = null;
        }

        // Close device
        try {
            if (this.device?.opened) {
                logger.debug('[WebUSB-FTDI] Closing device...');
                await this.device.close();
            }
        } catch (error) {
            logger.warn('[WebUSB-FTDI] Error closing device:', error);
        } finally {
            this.device = null;
            this.endpointIn = null;
            this.endpointOut = null;
        }

        logger.debug('[WebUSB-FTDI] Cleanup complete');
    }

    /**
     * Send MethodScript code to device
     */
    public async sendScript(script: string): Promise<void> {
        if (this.state !== ConnectionState.Connected) {
            throw new DeviceDisconnectedError(DEVICE_TYPE);
        }

        if (!this.endpointOut) {
            throw new DeviceDisconnectedError(DEVICE_TYPE, 'Output endpoint not available');
        }

        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(script);

            logger.debug(`[WebUSB-FTDI] Sending ${data.byteLength} bytes to device...`);

            // Transfer data to device
            if (!this.device) {
                throw new DeviceDisconnectedError(DEVICE_TYPE, 'Device not available');
            }
            await this.device.transferOut(this.endpointOut.endpointNumber, data);

            this.dispatchEvent(
                new CustomEvent('dataSent', {
                    detail: { data: script, bytes: data.byteLength },
                }),
            );

            logger.debug('[WebUSB-FTDI] Script sent successfully');
        } catch (error) {
            const errorMsg = toErrorMessage(error);
            logger.error('[WebUSB-FTDI] Failed to send script:', error);
            this.dispatchEvent(
                new CustomEvent('error', {
                    detail: { message: `Failed to send: ${errorMsg}` },
                }),
            );
            if (error instanceof DeviceError) {
                throw error;
            }
            throw new DeviceCommunicationError(DEVICE_TYPE, `Failed to send script: ${errorMsg}`, error);
        }
    }

    /**
     * Start reading data from device
     */
    private async startReading(): Promise<void> {
        if (!this.endpointIn) {
            logger.error('[WebUSB-FTDI] Cannot start reading: no input endpoint');
            return;
        }

        logger.debug('[WebUSB-FTDI] Read loop started');
        this.keepReading = true;

        try {
            while (this.keepReading && this.state === ConnectionState.Connected) {
                try {
                    // Read from device (64 bytes is typical for FTDI)
                    if (!this.device) {
                        break;
                    }
                    const result = await this.device.transferIn(this.endpointIn.endpointNumber, 64);

                    if (result.status === 'ok' && result.data && result.data.byteLength > 2) {
                        // FTDI devices prepend 2 status bytes to each packet
                        // Byte 0: Modem status, Byte 1: Line status
                        const dataView = new Uint8Array(result.data.buffer);
                        const actualData = dataView.slice(2); // Skip status bytes

                        if (actualData.byteLength > 0) {
                            const decoder = new TextDecoder();
                            const text = decoder.decode(actualData);

                            logger.debug('[WebUSB-FTDI] Received:', text);

                            this.dispatchEvent(
                                new CustomEvent('dataReceived', {
                                    detail: { data: text },
                                }),
                            );
                        }
                    }
                } catch (error) {
                    if (this.keepReading) {
                        const errorMsg = toErrorMessage(error);
                        logger.error('[WebUSB-FTDI] Error reading from device:', error);
                        this.dispatchEvent(
                            new CustomEvent('error', {
                                detail: { message: `Read error: ${errorMsg}` },
                            }),
                        );
                        this.keepReading = false;
                        await this.cleanup();
                        this.setState(ConnectionState.Disconnected);
                        this.dispatchEvent(new CustomEvent('disconnected'));
                        break;
                    }
                }
            }
        } finally {
            logger.debug('[WebUSB-FTDI] Read loop stopped');
        }
    }
}
